import type {
	JSResourceOptions,
	Loader,
	PreloadableJSResourceReference,
	SSRImportLiftableJSResourceOptions,
	SSRIncludeInShellOnlyOptions,
} from '../../types';
import { createCache } from '../../utils/create-cache';
import { PreloadableJSResource } from '../preloadable-js-resource';
import { postTask } from '../scheduler';

const cache = createCache(new Map<string, PreloadableJSResourceReference<any>>());

type UserVisibleOpts = JSResourceOptions &
	SSRImportLiftableJSResourceOptions &
	SSRIncludeInShellOnlyOptions;

/** Message when a string path reaches runtime (expected: build rewrites it to a loader). */
const RUNTIME_STRING_ERROR =
	'JSResourceForUserVisible was called with a string path at runtime. ' +
	'Ensure @atlassian/react-async-transformer is applied to transform this to a dynamic import.';

export function JSResourceForUserVisible<T = unknown>(
	path: string,
	opts?: UserVisibleOpts,
): PreloadableJSResourceReference<T>;

export function JSResourceForUserVisible<T = unknown>(
	loader: Loader<T>,
	opts?: UserVisibleOpts,
): PreloadableJSResourceReference<T>;

export function JSResourceForUserVisible<T = unknown>(
	loaderOrPath: Loader<T> | string,
	opts?: UserVisibleOpts,
): PreloadableJSResourceReference<T> {
	if (typeof loaderOrPath === 'string') {
		throw new Error(`${RUNTIME_STRING_ERROR} String path: ${JSON.stringify(loaderOrPath)}`);
	}
	return cache.get(opts!.moduleId!, () =>
		PreloadableJSResource(() => postTask(loaderOrPath, { priority: 'user-blocking' }), opts!),
	);
}
